import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { PageHeader } from '../../components/layout/PageHeader';
import { ApiError } from '../../lib/api';
import {
  backfillLabel,
  filterOptions,
  filterRows,
  isSelectable,
  NO_FILTERS,
  selectedSuggestedIds,
  STATUS_LABEL,
  type ConnectionFilters,
} from '../../lib/ga4/connectionsView';
import type { Ga4ConnectionRow } from '../../types/ga4Connections';
import { useCurrentUser } from '../auth';
import { listAccounts } from '../accounts';
import {
  connectGa4Property,
  connectSuggestedGa4Properties,
  disconnectGa4Property,
  getGa4DiscoveryStatus,
  listGa4Connections,
  startGa4Discovery,
} from './api';

// Operational → GA4 connections (/ga4-connections, admin-only).
// Brief: _architect/run-2026-11-12-ga4-connections.md
//
// ⚠️ THE ONLY MANUAL STEP IS GRANTING VIEWER IN GA4 — nothing here can do that, and
// the box below says exactly how. Everything after (discover, match, connect,
// backfill) happens here with no code change and no deploy.
// ⚠️ A SUGGESTION IS NOT A CONNECTION: nothing connects until someone clicks.

const errText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

const STATUS_STYLE: Record<Ga4ConnectionRow['status'], string> = {
  connected: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  suggested: 'bg-sky-50 text-sky-800 border-sky-200',
  'needs-choice': 'bg-amber-50 text-amber-900 border-amber-200',
  unmatched: 'bg-slate-50 text-slate-700 border-slate-200',
  'access-removed': 'bg-red-50 text-red-800 border-red-200',
};

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (mins < 2) return 'just now';
  if (mins < 120) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

export function Ga4ConnectionsPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const isAdmin = me.role === 'admin';
  const [filters, setFilters] = useState<ConnectionFilters>(NO_FILTERS);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [execution, setExecution] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [choosing, setChoosing] = useState<Record<string, string>>({});

  const data = useQuery({ queryKey: ['ga4-connections'], queryFn: listGa4Connections, enabled: isAdmin });
  const accounts = useQuery({ queryKey: ['accounts', 'all'], queryFn: listAccounts, enabled: isAdmin, staleTime: 5 * 60_000 });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['ga4-connections'] });

  // Poll "Discover now" until its execution settles, then reload the table.
  const status = useQuery({
    queryKey: ['ga4-connections', 'discover-status', execution],
    queryFn: () => getGa4DiscoveryStatus(execution!),
    enabled: isAdmin && execution !== null,
    refetchInterval: (q) => (q.state.data?.status === 'running' || !q.state.data ? 4000 : false),
  });
  const settled = status.data && status.data.status !== 'running' ? status.data.status : null;
  useEffect(() => {
    if (settled) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const discover = useMutation({
    mutationFn: startGa4Discovery,
    onMutate: () => {
      setError(null);
      setMessage(null);
    },
    onSuccess: (r) => setExecution(r.executionName),
    onError: (e) => setError(errText(e, 'Could not start discovery.')),
  });
  const connect = useMutation({
    mutationFn: (v: { propertyId: string; accountId: string }) => connectGa4Property(v.propertyId, v.accountId),
    onMutate: () => setError(null),
    onSuccess: () => {
      setMessage('Connected. History fills on the next job run.');
      refresh();
    },
    onError: (e) => setError(errText(e, 'Could not connect.')),
  });
  const bulk = useMutation({
    mutationFn: (ids: string[]) => connectSuggestedGa4Properties(ids),
    onMutate: () => setError(null),
    onSuccess: (r) => {
      setTicked(new Set());
      setMessage(`Connected ${r.connected.length}${r.skipped.length ? `; skipped ${r.skipped.length} (${r.skipped.map((s) => s.reason).join(' ')})` : ''}.`);
      refresh();
    },
    onError: (e) => setError(errText(e, 'Could not connect the selected properties.')),
  });
  const disconnect = useMutation({
    mutationFn: (accountId: string) => disconnectGa4Property(accountId),
    onMutate: () => setError(null),
    onSuccess: () => {
      setMessage('Disconnected. The history already pulled is kept.');
      refresh();
    },
    onError: (e) => setError(errText(e, 'Could not disconnect.')),
  });

  const rows = data.data?.rows ?? [];
  const visible = useMemo(() => filterRows(rows, filters), [rows, filters]);
  const options = useMemo(() => filterOptions(rows), [rows]);
  const selectedIds = selectedSuggestedIds(visible, ticked);
  const accountOptions = useMemo(
    () => (accounts.data ?? []).map((a) => ({ value: a.id, label: a.name, description: a.websiteUrl ?? undefined })),
    [accounts.data],
  );

  if (!isAdmin) return <p className="text-sm text-slate-600">Only admins can use GA4 connections.</p>;

  const sa = data.data?.serviceAccountEmail ?? 'reporting-pull@kyck-agency-prod.iam.gserviceaccount.com';
  const discovering = discover.isPending || (execution !== null && !settled);

  return (
    <div className="space-y-8">
      <PageHeader
        title="GA4 connections"
        description="Every GA4 property Kyck can see, which account it belongs to, and whether it is being pulled."
      />

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => discover.mutate()} disabled={discovering}>
            {discovering ? 'Discovering…' : 'Discover now'}
          </Button>
          <span className="text-sm text-slate-600">Last discovered: {data.data ? ago(data.data.lastDiscoveredAt) : '…'}</span>
          {settled === 'failed' && <span className="text-sm text-red-700">The last discovery run failed — see the job’s logs.</span>}
          {settled === 'succeeded' && <span className="text-sm text-emerald-700">Discovery finished.</span>}
        </div>
        <div className="max-w-3xl rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800">
          <p className="font-medium">To add a property, grant Viewer in GA4. That is the only manual step.</p>
          <p className="mt-1">
            In GA4: <b>Admin → Account access management</b> (covers every property in that GA4 account) or{' '}
            <b>Property access management</b> → <b>+</b> → <b>Add users</b> → paste this address → <b>Viewer</b>.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="rounded bg-white px-2 py-1 text-xs">{sa}</code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(sa).then(() => setCopied(true));
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-600">Then click Discover now. A new grant also appears by itself within a day.</p>
        </div>
      </section>

      {message && <p className="text-sm text-emerald-700">{message}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      <section className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <SelectField
              aria-label="Status"
              value={filters.status}
              onValueChange={(v) => setFilters((f) => ({ ...f, status: v as ConnectionFilters['status'] }))}
              options={[{ value: 'all', label: 'All statuses' }, ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))]}
            />
          </div>
          <div className="w-52">
            <SelectField
              aria-label="GA4 account"
              value={filters.ga4Account}
              onValueChange={(v) => setFilters((f) => ({ ...f, ga4Account: v }))}
              options={[{ value: '', label: 'Any GA4 account' }, ...options.ga4Accounts.map((a) => ({ value: a, label: a }))]}
            />
          </div>
          <div className="w-52">
            <SelectField
              aria-label="Industry"
              value={filters.industry}
              onValueChange={(v) => setFilters((f) => ({ ...f, industry: v }))}
              options={[{ value: '', label: 'Any industry' }, ...options.industries.map((a) => ({ value: a, label: a }))]}
            />
          </div>
          <input
            aria-label="Search"
            className="h-9 w-56 rounded-md border border-slate-300 px-3 text-sm"
            placeholder="Search properties…"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          />
          <Button onClick={() => bulk.mutate(selectedIds)} disabled={selectedIds.length === 0 || bulk.isPending}>
            Connect selected ({selectedIds.length})
          </Button>
        </div>

        {data.isLoading ? (
          <CenteredSpinner />
        ) : data.error ? (
          <p className="text-sm text-red-600">{errText(data.error, 'Couldn’t load GA4 connections.')}</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-slate-600">
            {rows.length === 0 ? 'No properties discovered yet. Grant Viewer, then click Discover now.' : 'No properties match these filters.'}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="w-8 p-2" />
                  <th className="p-2">GA4 property</th>
                  <th className="p-2">Website</th>
                  <th className="p-2">Kyck account</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Health</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.propertyId} className="border-t border-slate-100 align-top">
                    <td className="p-2">
                      {isSelectable(r) && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.displayName}`}
                          checked={ticked.has(r.propertyId)}
                          onChange={(e) =>
                            setTicked((s) => {
                              const n = new Set(s);
                              if (e.target.checked) n.add(r.propertyId);
                              else n.delete(r.propertyId);
                              return n;
                            })
                          }
                        />
                      )}
                    </td>
                    <td className="p-2">
                      <div className="font-medium text-slate-900">{r.displayName}</div>
                      <div className="text-xs text-slate-500">
                        {r.propertyId}
                        {r.ga4AccountName ? ` · ${r.ga4AccountName}` : ''}
                      </div>
                    </td>
                    <td className="max-w-[16rem] truncate p-2 text-xs text-slate-700" title={r.websites.join(', ')}>
                      {r.websites.length ? r.websites.join(', ') : 'No website stream'}
                    </td>
                    <td className="p-2">
                      {r.connectedTo ? (
                        <a className="text-sky-700 underline" href={`/accounts/${r.connectedTo.accountId}?tab=connections`}>
                          {r.connectedTo.accountName}
                        </a>
                      ) : r.suggestion ? (
                        <span>
                          {r.suggestion.accountName}{' '}
                          <span className="text-xs text-slate-500">({r.suggestion.reason === 'subdomain' ? 'subdomain' : 'domain match'})</span>
                        </span>
                      ) : r.candidates.length > 0 ? (
                        <span className="text-xs text-slate-600">{r.candidates.map((c) => c.accountName).join(', ')}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                      {r.note && <div className="mt-1 text-xs text-slate-500">{r.note}</div>}
                    </td>
                    <td className="p-2">
                      <span className={`inline-block rounded border px-2 py-0.5 text-xs ${STATUS_STYLE[r.status]}`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                      {r.codeConfigured && <div className="mt-1 text-xs text-slate-500">Configured in code</div>}
                    </td>
                    <td className="p-2 text-xs text-slate-700">
                      {backfillLabel(r) && <div>{backfillLabel(r)}</div>}
                      {(r.backfill || r.codeConfigured) && (
                        <div className={r.pullStale ? 'text-amber-800' : ''}>
                          Last pull: {ago(r.lastPulledAt)}
                          {r.pullStale ? ' — overdue' : ''}
                        </div>
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex min-w-[15rem] flex-col gap-2">
                        {r.status === 'suggested' && r.suggestion && (
                          <Button
                            size="sm"
                            disabled={connect.isPending}
                            onClick={() => connect.mutate({ propertyId: r.propertyId, accountId: r.suggestion!.accountId })}
                          >
                            Connect
                          </Button>
                        )}
                        {!r.codeConfigured && r.status !== 'connected' && r.status !== 'access-removed' && (
                          <div className="flex items-center gap-2">
                            <div className="w-48">
                              <Combobox
                                aria-label={`Choose account for ${r.displayName}`}
                                value={choosing[r.propertyId] ?? ''}
                                onValueChange={(v) => setChoosing((c) => ({ ...c, [r.propertyId]: v }))}
                                options={accountOptions}
                                placeholder="Choose account…"
                                searchPlaceholder="Search accounts…"
                                size="sm"
                              />
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!choosing[r.propertyId] || connect.isPending}
                              onClick={() => connect.mutate({ propertyId: r.propertyId, accountId: choosing[r.propertyId]! })}
                            >
                              Connect
                            </Button>
                          </div>
                        )}
                        {r.connectedTo && !r.codeConfigured && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={disconnect.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Disconnect ${r.displayName} from ${r.connectedTo!.accountName}? It stops pulling; the history already stored is kept.`,
                                )
                              ) {
                                disconnect.mutate(r.connectedTo!.accountId);
                              }
                            }}
                          >
                            Disconnect
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
